// Wrapper to handle the locations API

import { getLocations } from '@/api/widget/locations'
import { asTrimmedId } from '@/utils/ids'
import {
  Coords,
  EcomLocation,
  LocationsRequest,
  LocationsResponse,
  LocationType,
  LoyaltyLocation,
  MenuType,
  SubEcomLocation,
} from '@/types/widget'
import { isEmbedded } from '@/config'
import { showErrorToast } from '@/utils'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { getInitialUIDs } from './useSessionStore'
import { getUserDeviceLocation } from './useUserDeviceLocationStore'
import { isDemoMode } from './useWidgetStore'

type LocationsStoreState = {
  loaded: boolean
  loading: boolean
  data: LocationsResponse | undefined
  error: string | undefined
  abortController: AbortController
}

type LocationsStoreActions = {
  initialize: () => Promise<LocationsResponse | undefined>
  fetchLocations: (
    payload: LocationsRequest,
    locType?: LocationType,
    force?: boolean
  ) => Promise<LocationsResponse | undefined>
}

type LocationsStore = LocationsStoreState & LocationsStoreActions

const initialState: LocationsStoreState = {
  loaded: false,
  loading: false,
  data: undefined,
  error: undefined,
  abortController: new AbortController(),
}

const defaultCoords: Coords = {
  latitude: 0,
  longitude: 0,
  accuracy: 0,
}

export const useLocationsStore = create<LocationsStore>()(
  devtools(
    (set, get) => ({
      ...initialState,
      initialize: async () => {
        const uids = getInitialUIDs()
        const coords = getUserDeviceLocation() ?? defaultCoords
        const preview = isDemoMode()
        return await get().fetchLocations({ uids, coords, preview })
      },
      fetchLocations: async (
        payload: LocationsRequest,
        locType: LocationType = 'all',
        force: boolean = false
      ) => {
        if (get().loading && !force) {
          return undefined
        }
        // Abort the previous request if it is still running
        if (!get().abortController.signal.aborted) {
          get().abortController.abort()
        }
        // Create a new abort controller for the request
        const abortController = new AbortController()
        set({ loading: true, abortController }, false, 'fetchLocations/start')
        // Fetch the locations
        const [data, error] = await getLocations(payload, locType, { signal: abortController.signal })
        if (error) {
          set({ error, loading: false }, false, 'fetchLocations/error')

          if (error.includes('aborted')) {
            return undefined
          }

          if (!isEmbedded()) {
            showErrorToast(error)
          }

          return undefined
        }
        set({ data, loading: false, loaded: true }, false, 'fetchLocations/success')
        return data
      },
    }),
    { name: 'LocationsStore', enabled: process.env.NODE_ENV === 'development' }
  )
)

const defaultLocationsResponse: LocationsResponse = {
  ecomLocations: [],
  loyaltyLocations: [],
  enabled: false,
  code: 0,
  success: false,
}

/**
 * Utility function for accessing the locations data
 * Note: You must ensure the data is loaded before accessing it.
 * @returns The locations data. If the data is not loaded, it will return an empty locations response.
 */
export function getLocationsResponseData(): LocationsResponse {
  const data = useLocationsStore.getState().data
  return data ?? defaultLocationsResponse
}

/**
 * Utility function to determine if a globalID belongs to a UID
 * @param globalID The globalID of the location
 * @param uid The UID of the user
 * @returns True if the globalID belongs to the UID, false otherwise.
 */
export function doesEcomLocationBelongToUID(
  globalID: string,
  uid: string,
  locationData: EcomLocation[] = getLocationsResponseData()?.ecomLocations ?? []
): boolean {
  return locationData.some((location) => location.globalID === globalID && location.uid === uid)
}

/**
 * Utility function to get the locations for a UID
 * @param uid The UID of the user
 * @returns The locations for the UID.
 */
export function getEcomLocationsForUID(
  uid: string,
  locationData: EcomLocation[] = getLocationsResponseData()?.ecomLocations ?? []
): EcomLocation[] {
  return locationData.filter((location) => location.uid === uid)
}

/**
 * Utility function to get the location by globalID
 * @param globalID The globalID of the location
 * @returns The location by globalID.
 */
export function getEcomLocationByGlobalID(
  globalID: string,
  locationData: EcomLocation[] = getLocationsResponseData()?.ecomLocations ?? []
): EcomLocation | undefined {
  return locationData.find((location) => location.globalID === globalID)
}

/**
 * Utility function to get the sub ecom location by globalID
 * @param globalID The globalID of the location
 * @returns The sub ecom location by globalID.
 */
export function getSubEcomLocationByGlobalIDAndMenuType(
  globalID: string,
  menuType: MenuType,
  locationData: EcomLocation[] = getLocationsResponseData()?.ecomLocations ?? []
): SubEcomLocation | undefined {
  const location = getEcomLocationByGlobalID(globalID, locationData)
  if (!location) {
    return undefined
  }
  return location.subEcomLocations.find((subEcomLocation) => subEcomLocation.menuType === menuType)
}

/**
 * Utility function to get the loyalty location by UID
 * @param uid The UID of the user
 * @returns The loyalty location by UID.
 */
export function getLoyaltyLocationByUID(
  uid: string,
  locationData: LoyaltyLocation[] = getLocationsResponseData()?.loyaltyLocations ?? []
): LoyaltyLocation | undefined {
  return locationData.find((location) => location.uid === uid)
}

/**
 * Utility function to get the storeID by globalID
 * @param globalID The globalID of the location
 * @returns The storeID by globalID.
 */
export function getStoreIDByGlobalID(
  globalID: string,
  locationData: EcomLocation[] = getLocationsResponseData()?.ecomLocations ?? []
): string | undefined {
  const location = getEcomLocationByGlobalID(globalID, locationData)
  return asTrimmedId(location?.storeID)
}

export type StoreLocationQuery = {
  name?: string
  id?: string
  globalID?: string
  matchAny?: boolean
}

/**
 * Resolves an ecom location from a flexible query.
 * Priority: globalID > id (storeID) > name (case-insensitive).
 *
 * By default, only the highest-priority provided field is checked.
 * With matchAny: true, falls through to the next field if no match is found.
 */
export function resolveEcomLocation(query: StoreLocationQuery): EcomLocation | undefined {
  const locations = getLocationsResponseData().ecomLocations ?? []
  const fallthrough = query.matchAny === true

  if (query.globalID) {
    const match = locations.find((loc) => loc.globalID === query.globalID)
    if (match || !fallthrough) return match
  }
  if (query.id) {
    const match = locations.find((loc) => asTrimmedId(loc.storeID) === asTrimmedId(query.id))
    if (match || !fallthrough) return match
  }
  if (query.name) {
    return findLocationByName(locations, query.name)
  }
  return undefined
}

function normalizeLocationKey(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

function findLocationByName(locations: EcomLocation[], name: string): EcomLocation | undefined {
  const trimmed = name.trim()
  if (!trimmed) return undefined

  const exact = locations.find(
    (loc) => loc.locationName.trim().toLowerCase() === trimmed.toLowerCase()
  )
  if (exact) return exact

  const expected = normalizeLocationKey(trimmed)
  const matches = locations.filter((loc) => {
    const key = normalizeLocationKey(loc.locationName)
    return key === expected || key.endsWith(`-${expected}`) || expected.endsWith(`-${key}`)
  })
  if (matches.length === 1) return matches[0]

  const exactKey = matches.filter((loc) => normalizeLocationKey(loc.locationName) === expected)
  if (exactKey.length === 1) return exactKey[0]
  return undefined
}
